import React, { ComponentProps, useEffect, useState } from 'react';
import { skipToken } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import isNil, { notNil } from '../../../core/helpers/is-nil';
import { AddToCartSection } from '../../../visual-components/add-to-cart-section/add-to-cart-section';
import { WEIGHT_UNIT } from '../../../domain/article/services/weight';
import { getCurrencySymbol } from '../../../domain/article/services/currency';
import { ArticleDetail, ArticleSearch, Container } from '../../backend/openapi/api-types';
import { useArticleAvailabilityQuery, useArticlePreCartQuery } from '../../article/queries';
import { usePutArticleInCart } from '../mutations';
import { Select, SelectOption } from '../../../visual-components/select/select';
import { usePathBuilder } from '../../../core/routing/hooks/use-path-builder';
import { useFormatter } from '../../../core/formatter/use-formatter';
import { useContainer } from '../../article/hooks/use-container';
import { useStableFunction } from '../../../core/hooks/use-stable-function';
import { useTranslatedNormalizedErrors } from '../../backend/use-translated-errors';
import { useCartQuery } from '../queries';
import { DialogLayoutButton } from '../../../visual-components/dialog/dialog-layout-button';
import { Button } from '../../../visual-components/button/button';
import { ArticleMiniPreview } from '../../../visual-components/article-mini-preview/article-mini-preview';
import { CartDecyphering } from '../../../visual-components/add-to-cart-section/components/cart-decyphering';
import { LoadingSpinner } from '../../../visual-components/loading-spinner/loading-spinner';
import { Input } from '../../../visual-components/input/input';
import { Spacer } from '../../../visual-components/spacer/spacer';

const WEIGHT_INPUT_DEBOUNCE_TIMEOUT_MS = 500;

// TODO: @Simon proper type handling (article should always be of same type)
type DefaultProps = {
  article: ArticleDetail;
  state: ComponentProps<typeof AddToCartSection>['state'];
  moveOut?: boolean;
};

type DialogProps = {
  article: ArticleSearch;
  state: 'dialog';
  onClose: () => void;
};

type Props = DefaultProps | DialogProps;

// TODO: @Simon decide if the deferred loading of availability is acceptable
export const CartSection: React.FC<Props> = ({ article, state, ...rest }) => {
  const { t } = useTranslation();
  const { getFirstOptionalErrorMessage } = useTranslatedNormalizedErrors();
  const navigate = useNavigate();
  const { cartPath, formInquiryAboutArticle } = usePathBuilder();
  const { formatWeight, formatCurrency } = useFormatter();
  const { mapContainer } = useContainer();

  const articleId = article.id;

  const { data: cart } = useCartQuery();
  const hasCart = notNil(cart);
  const articleInCart = cart?.data.items.find((a) => a.article.id === articleId);
  // Use notNil cart further down as a guard for nice property access
  const isArticleInCart = notNil(articleInCart) && notNil(cart);

  /**
   * Input state stores what we show in the select.
   * If no container is selected, the BE will give us a container that should be shown.
   * However, we don't want another request. Therefore we have the user state that is only set iff the user chose something
   */
  const [selectedContainerOptionInput, setSelectedContainerOptionInput] = useState<Container | null>(null);
  const [selectedContainerOptionUser, setSelectedContainerOptionUser] = useState<Container | null>(null);
  const [selectedWeightInput, setSelectedWeightInput] = useState<string | null>(null);
  const [selectedWeight, setSelectedWeight] = useState<number | null>(null);

  const debouncedSelectedWeightInput = useStableFunction((selectedWeightInput: string | null) => {
    const hasInput = notNil(selectedWeightInput);
    if (!hasInput && isNil(selectedWeight)) {
      return;
    }

    if (!hasInput) {
      setSelectedWeight(null);
      return;
    }

    // Ignore formatting differences, e.g. 1.0 vs. 1. vs 1
    const input = selectedWeightInput.replace(',', '.');
    const weightInput = parseFloat(input);
    if (weightInput === selectedWeight) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setSelectedWeight(weightInput);
    }, WEIGHT_INPUT_DEBOUNCE_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  });

  useEffect(() => {
    return debouncedSelectedWeightInput(selectedWeightInput);
  }, [selectedWeightInput, debouncedSelectedWeightInput]);

  const updateSelectedWeightInput = useStableFunction((selectedWeight: number | null) => {
    // TODO: @Simon the parse float logic is used in multiple places, consider extracting
    const inputtedWeight = isNil(selectedWeightInput) ? null : parseFloat(selectedWeightInput.replace(',', '.'));
    if (inputtedWeight === selectedWeight) {
      return;
    }
    setSelectedWeightInput((selectedWeight ?? 0 > 0) ? `${selectedWeight}` : '');
  });

  useEffect(() => {
    updateSelectedWeightInput(selectedWeight);
  }, [selectedWeight, updateSelectedWeightInput]);

  const { data: articleAvailability } = useArticleAvailabilityQuery(articleId);
  const availability = articleAvailability?.data;

  const hasSelectedWeight = notNil(selectedWeight) && selectedWeight > 0;
  const {
    data: preCartArticleInformation,
    isLoading: preCartArticleInformationIsLoading,
    error: preCartArticleErrorInformation,
  } = useArticlePreCartQuery(
    articleId,
    hasSelectedWeight && availability?.isOnlineAvailable
      ? {
          weightKg: selectedWeight,
          container: selectedContainerOptionUser ?? undefined,
        }
      : skipToken,
  );
  const preCartDto = preCartArticleInformation?.data;
  const errorMessage = getFirstOptionalErrorMessage(preCartArticleErrorInformation);
  const hasPreCartError = notNil(errorMessage);

  const { mutateAsync: putArticleInCart, isPending: putArticleInCartIsPending } = usePutArticleInCart(articleId);

  const options: SelectOption<Container>[] =
    availability?.containerItemCombination?.map(({ container }) => mapContainer(container)) ?? [];

  const selectedContainer = selectedContainerOptionInput ?? null;

  const chooseContainerOption = (container: Container) => {
    setSelectedContainerOptionInput(container);
    setSelectedContainerOptionUser(container);
    if (isNil(selectedWeight) || selectedWeight <= 0) {
      const containerInfo = mapContainer(container);
      setSelectedWeight(containerInfo.weight);
    }
  };

  const updateSelectedContainerOptionState = useStableFunction(() => {});
  useEffect(() => {
    if (notNil(preCartDto?.container)) {
      setSelectedContainerOptionInput(preCartDto.container);
    }
  }, [preCartDto?.container, updateSelectedContainerOptionState]);

  const isReadyToPutArticleInCart = notNil(preCartDto);
  const canOnlyInquire = !availability?.isOnlineAvailable || (hasPreCartError && !preCartArticleInformationIsLoading);

  if (state === 'dialog') {
    // TODO: @Simon consider better typing
    const onClose = (rest as DialogProps).onClose;

    if (isNil(availability)) {
      return (
        <>
          <Spacer size="08" />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner variant="rectangle" />
          </div>
          <Spacer size="08" />
        </>
      );
    }
    return (
      <DialogLayoutButton
        buttons={
          <>
            <Button icon="cross" variant="secondary" onClick={onClose}>
              {t('global.cancel')}
            </Button>
            <Button
              disabled={!isReadyToPutArticleInCart || putArticleInCartIsPending}
              icon="check"
              onClick={async () => {
                // TODO: @Simon this is copied from further below
                // TODO: @Simon consider better error handling
                if (isNil(selectedContainer)) {
                  throw new Error('InvalidState. No container selected.');
                }
                const erpContainerArticleId = preCartDto?.erpContainerArticleId ?? '';
                await putArticleInCart({
                  erpContainerArticleId,
                  container: selectedContainer,
                  requestedWeight: selectedWeight ?? 0,
                });
                onClose();
              }}
            >
              {t('global.confirm')}
            </Button>
          </>
        }
      >
        <ArticleMiniPreview
          hasRendering={article.hasRendering}
          hexCode={article.hexCode ?? undefined}
          pimArticleId={article.id}
          series={article.series.name}
          seriesNumber={article.series.number}
        />
        <Spacer size="08" />
        <>
          {/* TODO: @Simon this is straight from add-to-cart-section */}
          <Input
            inputMode="numeric"
            placeholder={t('article.orderSection.weightPlaceholderText')}
            type="text"
            unit={WEIGHT_UNIT}
            value={selectedWeightInput ?? ''}
            inlineNotification={
              notNil(errorMessage)
                ? {
                    type: 'warning',
                    message: errorMessage,
                  }
                : undefined
            }
            onChange={(e) => {
              const value = e.target.value;
              setSelectedWeightInput(value);
            }}
          />
          <Select
            ariaLabel={t('article.orderSection.selectPlaceholderText')}
            options={options}
            placeholder={t('article.orderSection.selectPlaceholderText')}
            value={selectedContainer ?? undefined}
            onChange={(value) => {
              // TODO: @Simon consider better typing
              chooseContainerOption(value as Container);
            }}
          />
        </>
        {isNil(preCartDto) ? (
          preCartArticleInformationIsLoading ? (
            <LoadingSpinner variant="line" />
          ) : undefined
        ) : (
          <CartDecyphering
            articleUnit={WEIGHT_UNIT}
            formattedAmount={formatWeight(preCartDto.weightKg)}
            formattedPrice={formatCurrency(preCartDto.totalPrice)}
            priceUnit={getCurrencySymbol(preCartDto.currency)}
            secondaryText={`${preCartDto.neededContainers} × ${mapContainer(preCartDto.container).label} ${t(
              'global.costPerUnit',
              {
                currency: getCurrencySymbol(preCartDto.currency),
                unit: WEIGHT_UNIT,
                amount: preCartDto.pricePerUnit,
              },
            )}`}
          />
        )}
      </DialogLayoutButton>
    );
  }

  return hasCart ? (
    <AddToCartSection
      articleNumber={article.id}
      articleUnit={WEIGHT_UNIT}
      disabled={canOnlyInquire || articleInCart ? false : !isReadyToPutArticleInCart || putArticleInCartIsPending}
      disabledInput={isArticleInCart}
      hexColor={article.hexCode ?? undefined}
      moveOut={(rest as DefaultProps).moveOut}
      selectOptions={options}
      selectPlaceholderText={t('article.orderSection.selectPlaceholderText')}
      selectedOption={selectedContainer ?? undefined}
      series={article.series.name}
      state={state}
      titleText={isArticleInCart ? t('article.orderSection.inCart.title') : t('article.orderSection.title')}
      warningText={errorMessage}
      weightPlaceholderText={t('article.orderSection.weightPlaceholderText')}
      weightValue={selectedWeightInput ?? ''}
      actionButton={
        canOnlyInquire
          ? {
              icon: 'mail',
              text: t('global.request'),
              onClick: () => {
                navigate(
                  formInquiryAboutArticle(articleId, {
                    weightKg: isNil(selectedWeight) ? undefined : `${selectedWeight}`,
                    container: selectedContainer ?? undefined,
                  }),
                );
              },
            }
          : articleInCart
            ? {
                icon: 'arrow-right',
                text: t('article.orderSection.inCart.editPosition'),
                onClick: () => {
                  navigate(cartPath());
                },
              }
            : {
                icon: 'cart',
                text: t('article.orderSection.addToCart'),
                onClick: () => {
                  // TODO: @Simon consider better error handling
                  if (isNil(selectedContainer)) {
                    throw new Error('InvalidState. No container selected.');
                  }
                  const erpContainerArticleId = preCartDto?.erpContainerArticleId ?? '';
                  putArticleInCart({
                    erpContainerArticleId,
                    container: selectedContainer,
                    requestedWeight: selectedWeight ?? 0,
                  });
                },
              }
      }
      availability={
        isNil(availability)
          ? {
              state: 'loading',
            }
          : availability.isOnlineAvailable
            ? {
                tagText: t('availability.available'),
                filled: true,
              }
            : {
                tagText: t('availability.uponRequest'),
                filled: false,
              }
      }
      explainer={
        articleInCart
          ? {
              text: t('article.orderSection.inCart.explainer.text'),
              link: {
                children: t('cart.documentTitle'),
                to: cartPath(),
              },
            }
          : {
              title: t('article.orderSection.explainer.title'),
              text: t('article.orderSection.explainer.text'),
              link: {
                children: t('article.orderSection.explainer.linkText'),
                to: formInquiryAboutArticle(articleId),
              },
            }
      }
      orderSummary={
        isNil(preCartDto)
          ? preCartArticleInformationIsLoading
            ? {
                state: 'loading',
              }
            : isArticleInCart
              ? {
                  secondaryText: `${articleInCart.quantity} × ${mapContainer(articleInCart.container).label} ${t(
                    'global.costPerUnit',
                    {
                      currency: getCurrencySymbol(cart.data.currency.code),
                      unit: WEIGHT_UNIT,
                      amount: articleInCart.pricePerKg / 100,
                    },
                  )}`,
                  formattedAmount: formatWeight(articleInCart.weightKg),
                  formattedPrice: formatCurrency(articleInCart.lineTotal.amount / 100),
                  priceUnit: getCurrencySymbol(cart.data.currency.code),
                }
              : undefined
          : {
              secondaryText: `${preCartDto.neededContainers} × ${mapContainer(preCartDto.container).label} ${t(
                'global.costPerUnit',
                {
                  currency: getCurrencySymbol(preCartDto.currency),
                  unit: WEIGHT_UNIT,
                  amount: preCartDto.pricePerUnit,
                },
              )}`,
              formattedAmount: formatWeight(preCartDto.weightKg),
              formattedPrice: formatCurrency(preCartDto.totalPrice),
              priceUnit: getCurrencySymbol(preCartDto.currency),
            }
      }
      onSelectOption={(value) => {
        // TODO: @Simon consider better typing
        chooseContainerOption(value as Container);
      }}
      onWeightChange={(value) => {
        setSelectedWeightInput(value);
      }}
    />
  ) : null;
};
